import { useEffect, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $downloadSepaStatementStore, $initialAccountsStore } from '@lib/store';
import { cn, DateUtils, PaletteUtils } from '@shared/utils';
import { type AccountResponse, AccountType, StatementExportFileType } from '@shared/api';
import { ConfirmModal, SelectAccount } from '@widgets';
import { DateRangePickerInput, Button, Text, Spinner, ErrorMessage, BaseInput } from '@shared/widgets';
import AlertIcon from '@assets/icons/alert.svg';

const now = dayjs();
interface IForm {
  account: AccountResponse | null;
  date: { startDate?: Dayjs; endDate?: Dayjs };
  fileType: StatementExportFileType;
}
const DEFAULT_VALUES: IForm = { account: null, date: { startDate: now, endDate: now }, fileType: StatementExportFileType.Pdf };

export const StatementPaymentTab = () => {
  const accountStore = useUnit($initialAccountsStore.store);
  const downloadSepaStatementStore = useUnit($downloadSepaStatementStore.store);

  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);

  const { t } = useTranslation('statements');
  const { control, formState, handleSubmit, setValue } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });

  useEffect(() => {
    if (accountStore.data.length > 0) setValue('account', accountStore.data[0]);
  }, [accountStore.data]);

  useEffect(() => {
    if (downloadSepaStatementStore.error) setShowErrorModal(true);
    $downloadSepaStatementStore.events.clear();
  }, [downloadSepaStatementStore.error]);

  const submitHandler = async (data: IForm) =>
    await $downloadSepaStatementStore.events.downloadSepaStatement({
      name: `${data.account?.accountNumber}_${dayjs(data.date.startDate).format('YYYY-MM-DD')}_${dayjs(data.date.endDate).format('YYYY-MM-DD')}`,
      accountId: data.account!.id,
      fileType: data.fileType,
      from: DateUtils.formatToIsoString(data.date.startDate!),
      to: DateUtils.formatToIsoString(data.date.endDate!),
    });

  return (
    <form className='flex w-full flex-col' noValidate onSubmit={handleSubmit(submitHandler)}>
      {downloadSepaStatementStore.loading && <Spinner position />}
      <div
        className={cn(
          'flex flex-col bg-white p-6 dark:bg-soft-black',
          PaletteUtils.isVcfcPalette && 'rounded-xl dark:rounded-xl dark:border dark:border-gray-black',
        )}
      >
        <Controller
          name='account'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <SelectAccount
                loading={accountStore.loading}
                balanceFieldName='availableBalance'
                type={AccountType.Payment}
                accounts={accountStore.data!}
                currentAccount={field.value}
                onChange={(account) => field.onChange(account)}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
      </div>
      <div className='mt-6 flex gap-6 max-[1140px]:flex-col'>
        <div
          className={cn(
            'flex w-full flex-col bg-white p-4 text-center dark:bg-soft-black',
            PaletteUtils.isVcfcPalette && 'rounded-xl dark:rounded-xl dark:border dark:border-gray-black',
          )}
        >
          <Text size='xs'>{t('payment.dateLabel')}</Text>
          <div className='mt-2 flex justify-center gap-4'>
            <Controller
              name='date'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <BaseInput inputClassName='opacity-100' value={field.value.startDate?.format('DD.MM.YYYY')} readOnly />
                  <ErrorMessage text={!field.value.startDate ? error?.message : undefined} />
                </div>
              )}
            />
            <Controller
              name='date'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <BaseInput inputClassName='opacity-100' value={field.value.endDate?.format('DD.MM.YYYY')} readOnly />
                  <ErrorMessage text={!field.value.endDate ? error?.message : undefined} />
                </div>
              )}
            />
          </div>
          <div className='mt-3 flex self-center'>
            <Controller
              name='date'
              control={control}
              rules={{
                validate: (value) => {
                  if (!value?.startDate) return t('errors.form.required', { ns: 'common' });
                  if (!value?.endDate) return t('errors.form.required', { ns: 'common' });
                  return true;
                },
              }}
              render={({ field }) => (
                <DateRangePickerInput
                  dateFrom={field.value.startDate}
                  dateTo={field.value.endDate}
                  maxDate={now}
                  onChange={(startDate, endDate) => field.onChange({ startDate, endDate })}
                />
              )}
            />
          </div>
          <Button className='mx-auto mt-7 w-full max-w-[408px]' color='primary' type='submit' disabled={formState.isSubmitting}>
            {t('payment.buttons.generate')}
          </Button>
        </div>
      </div>
      <ConfirmModal
        isOpen={showErrorModal}
        icon={<AlertIcon />}
        title={t('payment.modal.title')}
        buttonTitle={t('payment.modal.button')}
        onClick={() => setShowErrorModal(false)}
      />
    </form>
  );
};
